import { GridColDef } from '@mui/x-data-grid'
import { PreferenceOrderValue, PreferenceSortValue, PreferenceVisibleValue } from '../../../interfaces/Commons/Preferences/gridPreferences';
import { useTranslation } from 'react-i18next';
import { useDates } from '../../useDates';
import { useFormatNumber } from '../../useFormatNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export const ColumnsGridMUI = () => {
  const [t, i18n] = useTranslation("global");
  const { TimeConverter } = useDates();
  const { setFormatNumberFromTable } = useFormatNumber();

  // For items in preference_value get type of column and return info for each column in type for material ui grid
  const GetColumnDefinitionByColumnsOrder = <T extends GridColDef>(preference_value: PreferenceOrderValue[] | undefined, entity: string, customColumnsData: GridColDef<any>[] = [], firstCustomColumn: boolean): GridColDef<T>[] => {
    let myColumnsOrder: GridColDef<T>[] = [];
    let myLastGenericColumns: GridColDef<T>[] = [];
    if(firstCustomColumn){
      myColumnsOrder = customColumnsData;
    }
    for (const column of preference_value ?? []) {
      const customColumn = customColumnsData.find((col) => col.field === column.column_name);

      if(myColumnsOrder.some((element) => element.field === column.column_name)){
        continue;
      }
      if (customColumn) {
        myColumnsOrder.push(customColumn as GridColDef<T>);
        continue;
      }
      if (column.column_name === 'creation_date' || column.column_name === 'last_update_date') {
        if (i18n.exists(`generic.${column.column_name}`)) {
          myLastGenericColumns.push({
            field: column.column_name,
            headerName: t(`generic.${column.column_name}`),
            headerClassName: 'header-grid-table',
            type: "date",
            flex: 1,
            valueGetter(params: any) {
              return TimeConverter(params.value);
            },
          },);
        }
      } else if (column.column_name === 'last_update_user') {
        if (i18n.exists(`generic.${column.column_name}`)) {
          myLastGenericColumns.push({
            field: column.column_name,
            headerName: t(`generic.${column.column_name}`),
            headerClassName: 'header-grid-table',
            flex: 1,
          },);
        }
      }
      else {
        if (i18n.exists(`${entity}.fields.${column.column_name}`)) {
          switch (column.column_type) {
            case 'int': {
              // TODO: default omit int columns but can appear in the future
              break;
            }
            case 'nvarchar': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
              },);
              break;
            }
            case 'datetime': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                type: "date",
                flex: 1,
                valueFormatter: (params) => {
                  if (!params.value) return undefined;
                  const date = new Date(params.value);
                  return date.toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  });
                }
              },);
              break;
            }
            case 'bit': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
                headerAlign: "center",
                type: "boolean",
                renderCell(params: { row: { [key: string]: any } }) {
                  let myColumnValue: boolean = params.row[column.column_name];
                  return myColumnValue ? <CheckCircleIcon /> : <CancelIcon />
                }
              },);
              break;
            }
            case 'numeric': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
                align: "left",
                headerAlign: "left",
                type: "number",
              });
              break;
            }
            default:
              // add nvarchar columns value
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
              },);
              break;
          }
        }
      }
    }

    return myColumnsOrder.concat(myLastGenericColumns);
  }

  const GetColumnDefinitionByColumnsVisible = async (myArrayPreferences: PreferenceVisibleValue[], isCustom: boolean = false): Promise<any> => {
    const columnsToForceFalse = ["creation_date", "last_update_user", "last_update_date"];

    const transformedArray: PreferenceVisibleValue[] = myArrayPreferences.map((item) => {
      const key = Object.keys(item)[0] as keyof PreferenceVisibleValue;
      return {
        column_name: key,
        column_visible: item[key] === 'true' || item[key] === true,
      };
    });

    const resultObject = transformedArray.reduce((acc, preference) => {
      if (!isCustom && columnsToForceFalse.includes(preference.column_name)) {
        acc[preference.column_name] = false;
      } else {
        acc[preference.column_name] = preference.column_visible;
      }
      return acc;
    }, {} as Record<string, boolean>);

    return await resultObject;
  };

  const GetColumnDefinitionByColumnsOrderProjects = <T extends GridColDef>(
    preference_value: PreferenceOrderValue[] | undefined,
    entity: string,
    customColumnsData: GridColDef<any>[] = [],
    projectExtrafieldsColumns: GridColDef<any>[] = []
  ): GridColDef<T>[] => {
    let myColumnsOrder: GridColDef<T>[] = [];
    let myLastGenericColumns: GridColDef<T>[] = [];

    for (const column of preference_value ?? []) {
      const customColumn = customColumnsData.find((col) => col.field === column.column_name)
        || projectExtrafieldsColumns.find((col) => col.field === column.column_name);

      if (customColumn) {
        myColumnsOrder.push(customColumn as GridColDef<T>);
        continue;
      }

      if (column.column_name === 'creation_date' || column.column_name === 'last_update_date') {
        if (i18n.exists(`generic.${column.column_name}`)) {
          myLastGenericColumns.push({
            field: column.column_name,
            headerName: t(`generic.${column.column_name}`),
            headerClassName: 'header-grid-table',
            type: "date",
            flex: 1,
            valueGetter(params: any) {
              return TimeConverter(params.value);
            },
          });
        }
      } else if (column.column_name === 'last_update_user') {
        if (i18n.exists(`generic.${column.column_name}`)) {
          myLastGenericColumns.push({
            field: column.column_name,
            headerName: t(`generic.${column.column_name}`),
            headerClassName: 'header-grid-table',
            flex: 1,
          });
        }
      } else {
        if (i18n.exists(`${entity}.fields.${column.column_name}`)) {
          switch (column.column_type) {
            case 'int': {
              // TODO: default omit int columns but can appear in the future
              break;
            }
            case 'nvarchar': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
              },);
              break;
            }
            case 'datetime': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                type: "date",
                flex: 1,
                valueFormatter: (params) => {
                  if (!params.value) return undefined;
                  const date = new Date(params.value);
                  return date.toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  });
                }
              },);
              break;
            }
            case 'bit': {
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
                type: "boolean",
                renderCell(params: { row: { [key: string]: any } }) {
                  let myColumnValue: boolean = params.row[column.column_name];
                  return myColumnValue ? <CheckCircleIcon /> : <CancelIcon />
                }
              },);
              break;
            }
            case 'numeric': {
              myColumnsOrder.push({
                field: t(`${entity}.fields.${column.column_name}`),
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
                align: "left",
                headerAlign: "left",
                type: "number",
              });
              break;
            }
            default:
              // add nvarchar columns value
              myColumnsOrder.push({
                field: column.column_name,
                headerName: t(`${entity}.fields.${column.column_name}`),
                headerClassName: 'header-grid-table',
                flex: 1,
              },);
              break;
          }
        }
      }
    }

    const existingFields = new Set([
      ...myColumnsOrder.map(col => col.field),
      ...myLastGenericColumns.map(col => col.field)
    ]);

    const remainingExtraColumns = projectExtrafieldsColumns.filter(
      (col) => !existingFields.has(col.field)
    );

    return [...myColumnsOrder, ...myLastGenericColumns, ...remainingExtraColumns];
  };

  const GetColumnDefinitionByColumnsSort = async (myArrayPreferences: PreferenceSortValue[], isCustom: boolean = false): Promise<any> => {

    const transformedArray: PreferenceSortValue[] = myArrayPreferences.map((item) => {
      return {
        field: item.field,
        sort: item.sort,
      };
    });

    return await transformedArray;
  };

  return {
    GetColumnDefinitionByColumnsOrder,
    GetColumnDefinitionByColumnsOrderProjects,
    GetColumnDefinitionByColumnsVisible,
    GetColumnDefinitionByColumnsSort,
  }
}
